import React from "react";
import { Alert, Form, InputNumber, Select, Space, Switch } from "antd";
import { useTranslation } from "react-i18next";

import {
  PaymentTypeOption,
  type Currency,
  type PricePaymentType,
  type Product,
  type ProductPrice,
  type ProductSubscriptionInterval,
} from "@fitness-app/data-models/entities/Product";

/**
 * Sentinel value of the price dropdown standing for "mint a price for the amount
 * I type in" (TRE-1766) — the backend creates it in Stripe on submit.
 */
export const CUSTOM_PRICE_OPTION = "__custom_price__";

/** "No access period" — a one-time price without one grants lifetime access. */
export const NO_ACCESS_PERIOD = "none";

export interface ManualOrderCustomPriceFormModel {
  customPriceType: PricePaymentType;
  customPriceInterval: ProductSubscriptionInterval | typeof NO_ACCESS_PERIOD;
  customPriceIntervalCount: number;
  skipImpactOnClientAccess: boolean;
}

export const customPriceDefaults: ManualOrderCustomPriceFormModel = {
  customPriceType: PaymentTypeOption.OneTime,
  customPriceInterval: "month",
  customPriceIntervalCount: 1,
  skipImpactOnClientAccess: false,
};

export const isCustomPriceSelected = (priceId: string | undefined | null): boolean => priceId === CUSTOM_PRICE_OPTION;

/** The product's own currency — a custom price must not silently switch it. */
export const getProductCurrency = (product: Pick<Product, "prices"> | null | undefined): Currency =>
  (product?.prices || []).find((price): price is ProductPrice => Boolean(price?.currency))?.currency ?? "pln";

export const buildCustomPricePayload = (formModel: ManualOrderCustomPriceFormModel, currency: Currency) => {
  const isRecurring = formModel.customPriceType === PaymentTypeOption.Recurring;
  const hasAccessPeriod = isRecurring || formModel.customPriceInterval !== NO_ACCESS_PERIOD;
  const interval = formModel.customPriceInterval === NO_ACCESS_PERIOD ? "month" : formModel.customPriceInterval;

  return {
    type: formModel.customPriceType,
    currency,
    interval: hasAccessPeriod ? interval : null,
    interval_count: hasAccessPeriod ? formModel.customPriceIntervalCount || 1 : null,
  };
};

/**
 * Only a one-time custom price can be settlement-only — a recurring one opens a
 * Stripe subscription whose cycles drive access on their own (the backend
 * rejects the combination too).
 */
export const resolveSkipImpactOnClientAccess = (
  formModel: ManualOrderCustomPriceFormModel,
  withCustomPrice: boolean,
): boolean =>
  Boolean(
    withCustomPrice && formModel.customPriceType !== PaymentTypeOption.Recurring && formModel.skipImpactOnClientAccess,
  );

const INTERVALS: ProductSubscriptionInterval[] = ["day", "week", "month", "year"];

interface Props {
  /** Watched value of `customPriceType` in the parent form. */
  priceType: PricePaymentType | undefined;
  /** Watched value of `customPriceInterval` in the parent form. */
  interval: ManualOrderCustomPriceFormModel["customPriceInterval"] | undefined;
  /** Watched value of `skipImpactOnClientAccess` in the parent form. */
  skipImpactOnClientAccess: boolean | undefined;
}

/**
 * Fields revealed after picking "Własna cena…". Shared by both manual-order
 * modals so the two copies cannot drift apart.
 */
export const ManualOrderCustomPriceFields = ({
  priceType,
  interval,
  skipImpactOnClientAccess,
}: Props): React.ReactElement => {
  const { t } = useTranslation("client");
  const form = Form.useFormInstance<ManualOrderCustomPriceFormModel>();
  const isRecurring = priceType === PaymentTypeOption.Recurring;
  const settlementOnly = !isRecurring && Boolean(skipImpactOnClientAccess);
  const withoutAccessPeriod = !isRecurring && !settlementOnly && interval === NO_ACCESS_PERIOD;

  const intervalOptions = INTERVALS.map((value) => ({ label: t(`order.customPriceInterval.${value}`), value }));

  // The two modes offer different options — "no access period" exists only for
  // a one-time price. Left alone, that value survives a switch to recurring as
  // a selected-but-invisible entry that passes the required check and resolves
  // to a monthly subscription nobody picked.
  const resetPeriod = () => form.setFieldsValue({ customPriceInterval: "month", customPriceIntervalCount: 1 });

  return (
    <>
      <Form.Item name="customPriceType" label={t("order.customPriceType")} rules={[{ required: true }]}>
        <Select
          onChange={resetPeriod}
          options={[
            { label: t("order.customPriceTypeOneTime"), value: PaymentTypeOption.OneTime },
            { label: t("order.customPriceTypeRecurring"), value: PaymentTypeOption.Recurring },
          ]}
        />
      </Form.Item>
      <Form.Item
        label={isRecurring ? t("order.customPriceBillingPeriod") : t("order.customPriceAccessPeriod")}
        required={isRecurring}
      >
        <Space.Compact style={{ width: "100%" }}>
          <Form.Item
            name="customPriceIntervalCount"
            noStyle
            // Disabled together with "no access period" — a required rule on a
            // field the user cannot edit would deadlock the form.
            rules={[{ required: !withoutAccessPeriod, type: "number", min: 1 }]}
          >
            <InputNumber min={1} precision={0} style={{ width: 90 }} disabled={withoutAccessPeriod} />
          </Form.Item>
          <Form.Item name="customPriceInterval" noStyle rules={[{ required: true }]}>
            <Select
              style={{ width: "100%" }}
              options={
                isRecurring
                  ? intervalOptions
                  : [{ label: t("order.customPriceNoAccessPeriod"), value: NO_ACCESS_PERIOD }, ...intervalOptions]
              }
            />
          </Form.Item>
        </Space.Compact>
      </Form.Item>
      {isRecurring ? null : (
        <Form.Item
          name="skipImpactOnClientAccess"
          label={t("order.customPriceSkipAccessImpact")}
          valuePropName="checked"
          extra={t("order.customPriceSkipAccessImpactHint")}
        >
          <Switch />
        </Form.Item>
      )}
      <Alert
        showIcon
        type={isRecurring || withoutAccessPeriod ? "warning" : "info"}
        message={
          isRecurring
            ? t("order.customPriceRecurringWarning")
            : settlementOnly
              ? t("order.customPriceSettlementOnlyInfo")
              : withoutAccessPeriod
                ? t("order.customPriceLifetimeWarning")
                : t("order.customPriceInfo")
        }
      />
    </>
  );
};
